import React, { Component } from "react";
import {
  Html5Qrcode,
  Html5QrcodeScanType,
  Html5QrcodeSupportedFormats,
} from "html5-qrcode";
import { Modal, ModalBody, Button } from "reactstrap";

class BarcodeScanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      error: null,
      lastScannedCode: null,
      scannerStarted: false,
    };
    this.html5QrCode = null;
    this.scannerId =
      "barcode-scanner-" + Math.random().toString(36).substr(2, 9);
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.setState({ isLoading: true, error: null, lastScannedCode: null });
      // Delay để DOM render xong
      setTimeout(() => this.startScanner(), 300);
    }

    if (!this.props.isOpen && prevProps.isOpen) {
      this.stopScanner();
    }
  }

  componentWillUnmount() {
    this.stopScanner();
  }

  startScanner = async () => {
    try {
      // Tạo instance mới mỗi lần
      this.html5QrCode = new Html5Qrcode(this.scannerId);

      // Config tối ưu cho EAN-13 và các loại barcode 1D
      const config = {
        fps: 15, // Tăng FPS để quét nhanh hơn
        qrbox: { width: 300, height: 150 }, // Khung hẹp cho barcode 1D
        aspectRatio: 1.777778, // 16:9
        disableFlip: false,
        // Chỉ định các format barcode cần nhận dạng
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.CODE_93,
          Html5QrcodeSupportedFormats.CODABAR,
          Html5QrcodeSupportedFormats.ITF,
          Html5QrcodeSupportedFormats.QR_CODE,
        ],
      };

      await this.html5QrCode.start(
        { facingMode: "environment" },
        config,
        this.handleScanSuccess,
        this.handleScanError
      );

      this.setState({ isLoading: false, scannerStarted: true });
      console.log("[BarcodeScanner] Started successfully");
    } catch (err) {
      console.error("[BarcodeScanner] Start error:", err);
      this.setState({
        error: this.getErrorMessage(err),
        isLoading: false,
      });
    }
  };

  stopScanner = async () => {
    if (this.html5QrCode && this.state.scannerStarted) {
      try {
        await this.html5QrCode.stop();
        console.log("[BarcodeScanner] Stopped");
      } catch (err) {
        console.log("[BarcodeScanner] Stop error (ignored):", err);
      }
      this.html5QrCode = null;
      this.setState({ scannerStarted: false });
    }
  };

  handleScanSuccess = (decodedText, decodedResult) => {
    // Tránh quét lại cùng một mã trong 2 giây
    if (decodedText === this.state.lastScannedCode) {
      return;
    }

    console.log(
      "[BarcodeScanner] Scanned:",
      decodedText,
      decodedResult.result.format?.formatName
    );

    this.setState({ lastScannedCode: decodedText });

    // Phát âm thanh beep (optional)
    this.playBeep();

    // Gọi callback
    this.props.onScan(decodedText);

    // Reset lastScannedCode sau 2 giây để có thể quét lại
    setTimeout(() => {
      this.setState({ lastScannedCode: null });
    }, 2000);
  };

  handleScanError = (errorMessage) => {
    // Bỏ qua lỗi "No barcode found" - đây là normal khi không có mã trong khung
    // Chỉ log các lỗi khác
    if (
      !errorMessage.includes("No MultiFormat Readers") &&
      !errorMessage.includes("NotFoundException")
    ) {
      // console.log('[BarcodeScanner] Scan error:', errorMessage);
    }
  };

  getErrorMessage = (err) => {
    const message = err?.message || err?.toString() || "";

    if (message.includes("NotAllowedError") || message.includes("Permission")) {
      return "Không được cấp quyền truy cập camera. Vui lòng cho phép truy cập camera trong cài đặt trình duyệt.";
    }
    if (
      message.includes("NotFoundError") ||
      message.includes("DevicesNotFound")
    ) {
      return "Không tìm thấy camera. Vui lòng kiểm tra thiết bị của bạn.";
    }
    if (
      message.includes("NotReadableError") ||
      message.includes("TrackStartError")
    ) {
      return "Camera đang được sử dụng bởi ứng dụng khác. Vui lòng đóng các ứng dụng khác đang dùng camera.";
    }
    if (message.includes("OverconstrainedError")) {
      return "Camera không hỗ trợ cấu hình yêu cầu.";
    }

    return "Không thể khởi động camera. Vui lòng thử lại.";
  };

  playBeep = () => {
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 1800;
      oscillator.type = "sine";
      gainNode.gain.value = 0.3;

      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 100);
    } catch (e) {
      // Ignore audio errors
    }
  };

  handleRetry = () => {
    this.setState({ error: null, isLoading: true, lastScannedCode: null });
    setTimeout(() => this.startScanner(), 300);
  };

  render() {
    const { isOpen, onClose } = this.props;
    const { isLoading, error } = this.state;

    return (
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        className="barcode-scanner-modal"
        centered
        size="lg"
        onClosed={this.stopScanner}
      >
        <ModalBody className="p-0">
          <div className="scanner-header d-flex justify-content-between align-items-center p-3 bg-dark text-white">
            <h5 className="mb-0">
              <i className="simple-icon-camera mr-2"></i>
              Quét mã Barcode / QR
            </h5>
            <Button close onClick={onClose} className="text-white" />
          </div>

          {error ? (
            <div className="p-5 text-center">
              <i
                className="simple-icon-exclamation text-danger"
                style={{ fontSize: "64px" }}
              />
              <p className="text-danger mt-4 mb-4" style={{ fontSize: "16px" }}>
                {error}
              </p>
              <Button color="primary" size="lg" onClick={this.handleRetry}>
                <i className="simple-icon-reload mr-2"></i>
                Thử lại
              </Button>
            </div>
          ) : (
            <div className="scanner-wrapper">
              {isLoading && (
                <div className="scanner-loading-overlay">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-3 mb-0">Đang khởi động camera...</p>
                </div>
              )}

              {/* Scanner container - html5-qrcode sẽ render vào đây */}
              <div
                id={this.scannerId}
                style={{
                  width: "100%",
                  minHeight: "400px",
                  background: "#000",
                }}
              />

              <div className="scanner-instructions p-3 bg-light text-center">
                <div className="mb-2">
                  <span className="badge badge-success mr-2">EAN-13</span>
                  <span className="badge badge-success mr-2">EAN-8</span>
                  <span className="badge badge-success mr-2">UPC</span>
                  <span className="badge badge-success mr-2">Code128</span>
                  <span className="badge badge-info">QR</span>
                </div>
                <small className="text-muted">
                  <i className="simple-icon-info mr-1"></i>
                  Đưa mã vạch vào khung hình. Giữ cố định và đảm bảo đủ ánh
                  sáng.
                </small>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }
}

export default BarcodeScanner;
